import { faArrowRight, faBriefcase } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { Badge } from '~/components/Global/Blocks/Badge/Badge'
import { Card } from '~/components/Global/Blocks/Card/Card'
import { MetaItem } from '~/components/Global/Blocks/Card/MetaItem'
import { LabelWithIcon } from '~/components/Global/Elements/LabelWithIcon/LabelWithIcon'
import { NameCircle } from '~/components/Global/Elements/NameCircle/NameCircle'
import type { ApplicantExperience } from '~/domains/applicants/types'
import { vars } from '~/styles/theme.css'
import { formatDateShort, formatRelativeTime } from '~/utils/date'
import { formatName } from '~/utils/names'

export const ExperienceCard = ({
  experience,
  onClick
}: {
  experience: ApplicantExperience
  onClick: () => void
}): ReactElement => {
  const companyLabel = experience.company?.name ?? experience.companyName ?? ''
  const roleLabel =
    experience.position ??
    experience.qualificationName ??
    experience.order?.qualification?.name ??
    ''
  const contractLabel =
    experience.contractTerm?.name ?? experience.order?.contractTerm?.name ?? ''

  const relativeEndTime = experience.end
    ? formatRelativeTime(new Date(experience.end), 2)
    : ''
  const applicantName = formatName(experience.applicant)

  const endDate = experience.end
    ? formatDateShort(new Date(experience.end))
    : ''
  const now = new Date()
  const soonThresholdDays = 7

  const startDate = experience.start
    ? formatDateShort(new Date(experience.start))
    : ''
  const dateLabel = (
    <>
      {startDate} <FontAwesomeIcon icon={faArrowRight} /> {endDate}
    </>
  )

  const getBadgeColor = () => {
    if (!experience.end || new Date(experience.end) < now)
      return vars.color.danger
    const diffDays =
      (new Date(experience.end).getTime() - now.getTime()) /
      (1000 * 60 * 60 * 24)
    return diffDays <= soonThresholdDays
      ? vars.color.warning
      : vars.color.success
  }

  const badgeProps = {
    title: relativeEndTime,
    backgroundColor: getBadgeColor()
  }

  const metas = [
    <MetaItem key="company">{companyLabel}</MetaItem>,
    <MetaItem key="contract" title={contractLabel}>
      {dateLabel}
    </MetaItem>,
    <MetaItem key="end" align="right">
      <Badge {...badgeProps} />
    </MetaItem>
  ]

  return (
    <Card
      onClick={onClick}
      icon={<NameCircle name={applicantName} size={40} />}
      title={applicantName}
      metaData={metas.filter(Boolean)}
    >
      <LabelWithIcon icon={faBriefcase}>{roleLabel}</LabelWithIcon>
    </Card>
  )
}
